/**
 * CollectionViewTemplate - Magazine-style collection view template
 *
 * Displays photo with optional text content below.
 * For read-only magazine-style collection viewing (no EXIF data).
 *
 * @module PhotoItem2/templates/CollectionViewTemplate
 */

import { cn } from '@/lib/utils';
import type { PhotoItem2TemplateProps } from '../PhotoItem2.types';
import { AdaptivePhoto } from '../../photos/AdaptivePhoto';

/**
 * CollectionViewTemplate component
 *
 * Renders photo in magazine-style layout with optional text content.
 * Simple, clean presentation for public collection viewing.
 */
export function CollectionViewTemplate({
  data,
  isSelected = false,
  selectable = false,
  zoom = 1.0, // NOTE: zoom prop no longer used - AdaptivePhoto uses container size
  onClick,
  onCritiqueClick: _onCritiqueClick, // Filter out non-DOM prop
  className,
  ...restProps
}: PhotoItem2TemplateProps) {
  void zoom; // Suppress unused variable warning - AdaptivePhoto uses container size instead
  void _onCritiqueClick; // Filter out - not a valid DOM prop

  // Feature 081: photo-versioning - Forward additional props (e.g., onContextMenu) to article
  return (
    <article
      className={cn(
        'w-full flex flex-col gap-4',
        className
      )}
      {...restProps}
    >
      {/* Photo */}
      <div
        onClick={onClick}
        className={cn(
          'relative w-full',
          selectable && 'cursor-pointer hover:opacity-90',
          isSelected && 'ring-4 ring-primary'
        )}
      >
        <AdaptivePhoto
          photo={data.photoData}
          alt={data.photoData.title || data.photoData.description || ''}
          className="w-full h-auto max-h-[800px]"
          imgClassName="w-full h-auto max-h-[800px] object-contain"
          trackingSource="collection_view"
          preferStorageUrl={true}
        />
      </div>

      {/* Text content below photo */}
      {data.textContent && (
        <div className="w-full">
          <p className="text-base leading-relaxed text-foreground">{data.textContent}</p>
        </div>
      )}
    </article>
  );
}
